<template>
  <div class="org-page org-studio">
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />

    <UpdateOrgDetailsDialog
      v-model="showEditDialog"
      :organization="organizationForDialog"
      @saved="onOrgDetailsSaved"
      @error="onOrgDetailsError"
    />
    <RequestCreditsDialog
      v-model="showRequestCreditsDialog"
      @saved="onCreditRequestSaved"
      @error="onCreditRequestError"
    />

    <div
      v-if="pageLoading"
      class="org-page__loading d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <template v-else>
      <div class="org-page__content org-studio__content">
        <!-- Identity masthead -->
        <header class="org-masthead">
          <div class="org-masthead__glow" aria-hidden="true" />
          <div class="org-masthead__mesh" aria-hidden="true" />

          <div class="org-masthead__top">
            <div class="org-masthead__brand">
              <div class="org-masthead__logo">
                <img
                  v-if="logoPreviewUrl"
                  :src="logoPreviewUrl"
                  alt=""
                  class="org-masthead__logo-img"
                />
                <div v-else class="org-masthead__logo-fallback">
                  <span>{{ orgInitials }}</span>
                </div>
              </div>

              <div class="org-masthead__copy min-w-0">
                <div class="org-masthead__eyebrow">
                  <span class="org-masthead__eyebrow-dot" />
                  Organisation profile
                </div>
                <div class="org-masthead__title-row">
                  <h1 class="org-masthead__title">
                    {{ displayOrganization.org_name || 'Organisation' }}
                  </h1>
                  <span
                    v-if="displayOrganization.org_status"
                    class="org-masthead__status"
                    :class="`org-masthead__status--${statusTone}`"
                  >
                    {{ statusDisplay }}
                  </span>
                </div>
                <p v-if="displayOrganization.org_tag_line" class="org-masthead__tagline">
                  {{ displayOrganization.org_tag_line }}
                </p>
                <p class="org-masthead__meta">
                  <v-icon size="14" icon="mdi-clock-outline" />
                  {{ lastUpdatedLabel }} {{ lastUpdatedDisplay }}
                </p>
              </div>
            </div>

            <div class="org-masthead__actions">
              <v-btn
                color="primary"
                prepend-icon="mdi-pencil-outline"
                class="text-none btn-primary-cta org-masthead__edit"
                rounded="lg"
                :disabled="!isSessionReady"
                @click="showEditDialog = true"
              >
                Edit profile
              </v-btn>
            </div>
          </div>

          <section class="org-ribbon" aria-label="Organisation overview">
            <div
              v-for="(stat, index) in orgOverviewCards"
              :key="stat.key"
              class="org-ribbon__item"
              :class="{
                'org-ribbon__item--accent': stat.key === 'credits_balance',
                'org-ribbon__item--divider': index > 0,
              }"
            >
              <span class="org-ribbon__icon" :class="`org-ribbon__icon--${stat.key}`">
                <v-icon :icon="stat.icon" size="16" />
              </span>
              <div class="org-ribbon__text">
                <span class="org-ribbon__label">{{ stat.label }}</span>
                <span class="org-ribbon__value">{{ stat.value }}</span>
              </div>
            </div>
          </section>
        </header>

        <nav v-if="outgoingEnabled" class="org-page__tabs" role="tablist" aria-label="Organisation sections">
          <button
            type="button"
            class="org-page__tab"
            :class="{ 'is-active': pageTab === 'details' }"
            role="tab"
            :aria-selected="pageTab === 'details'"
            @click="pageTab = 'details'"
          >
            Details
          </button>
          <button
            type="button"
            class="org-page__tab"
            :class="{ 'is-active': pageTab === 'settings' }"
            role="tab"
            :aria-selected="pageTab === 'settings'"
            @click="pageTab = 'settings'"
          >
            Settings
          </button>
        </nav>

        <!-- Studio brief — timeline bands (no card grid) -->
        <div v-show="pageTab === 'details'" class="org-studio__brief">
          <section v-if="descriptionText" class="org-studio__band org-studio__band--story">
            <div class="org-studio__rail" aria-hidden="true">
              <span class="org-studio__step">01</span>
              <span class="org-studio__spine" />
            </div>
            <div class="org-studio__panel">
              <header class="org-studio__head">
                <span class="org-studio__kicker">About</span>
                <h2 class="org-studio__title">Brand story</h2>
              </header>
              <blockquote class="org-studio__quote">{{ descriptionText }}</blockquote>
            </div>
          </section>

          <section v-if="contactRows.length" class="org-studio__band org-studio__band--reach">
            <div class="org-studio__rail" aria-hidden="true">
              <span class="org-studio__step">02</span>
              <span class="org-studio__spine" />
            </div>
            <div class="org-studio__panel">
              <header class="org-studio__head">
                <span class="org-studio__kicker">Reach</span>
                <h2 class="org-studio__title">Contact &amp; location</h2>
              </header>
              <div class="org-studio__flow">
                <article
                  v-for="(row, index) in contactRows"
                  :key="row.key"
                  class="org-studio__node"
                  :class="{ 'org-studio__node--wide': row.wide }"
                >
                  <span v-if="index > 0" class="org-studio__flow-link" aria-hidden="true" />
                  <span class="org-studio__node-icon">
                    <v-icon :icon="row.icon" size="18" />
                  </span>
                  <div class="org-studio__node-body">
                    <span class="org-studio__node-label">{{ row.label }}</span>
                    <a
                      v-if="row.href"
                      :href="row.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="org-studio__node-value org-studio__node-value--link"
                    >
                      {{ row.text }}
                    </a>
                    <span v-else class="org-studio__node-value">{{ row.text }}</span>
                  </div>
                </article>
              </div>
            </div>
          </section>

          <div class="org-studio__split">
            <section class="org-studio__band org-studio__band--prefs">
              <div class="org-studio__rail" aria-hidden="true">
                <span class="org-studio__step">03</span>
                <span class="org-studio__spine org-studio__spine--short" />
              </div>
              <div class="org-studio__panel">
                <header class="org-studio__head org-studio__head--row">
                  <div>
                    <span class="org-studio__kicker">Preferences</span>
                    <h2 class="org-studio__title">Call delivery</h2>
                  </div>
                  <v-icon size="20" class="org-studio__head-icon">mdi-phone-settings-outline</v-icon>
                </header>
                <div class="org-studio__stream">
                  <article
                    v-for="row in callPreferenceRows"
                    :key="row.key"
                    class="org-studio__stream-row"
                    :class="[
                      `org-studio__stream-row--${row.channel || 'rule'}`,
                      row.enabled ? 'org-studio__stream-row--on' : 'org-studio__stream-row--off',
                    ]"
                  >
                    <span class="org-studio__stream-icon">
                      <v-icon :icon="row.icon" size="18" />
                    </span>
                    <div class="org-studio__stream-copy">
                      <h3 class="org-studio__stream-label">{{ row.label }}</h3>
                      <p v-if="row.text" class="org-studio__stream-hint">{{ row.text }}</p>
                      <p v-else-if="row.isChannel && !row.enabled" class="org-studio__stream-hint">
                        Channel inactive
                      </p>
                      <div v-if="row.chips?.length" class="org-studio__tags">
                        <span
                          v-for="(chip, idx) in row.chips"
                          :key="`${row.key}-${idx}`"
                          class="org-studio__tag"
                        >
                          {{ chip }}
                        </span>
                      </div>
                    </div>
                    <span
                      class="org-studio__pill"
                      :class="row.enabled ? 'org-studio__pill--on' : 'org-studio__pill--off'"
                    >
                      {{ row.enabled ? 'On' : 'Off' }}
                    </span>
                  </article>
                </div>
              </div>
            </section>

            <section class="org-studio__band org-studio__band--leads">
              <div class="org-studio__rail" aria-hidden="true">
                <span class="org-studio__step">04</span>
              </div>
              <div class="org-studio__panel">
                <header class="org-studio__head org-studio__head--row">
                  <div>
                    <span class="org-studio__kicker">Leads</span>
                    <h2 class="org-studio__title">Business query alerts</h2>
                  </div>
                  <v-icon size="20" class="org-studio__head-icon">mdi-message-badge-outline</v-icon>
                </header>
                <div class="org-studio__stream">
                  <article
                    v-for="row in businessQueryRows"
                    :key="row.key"
                    class="org-studio__stream-row"
                    :class="[
                      `org-studio__stream-row--${row.channel}`,
                      row.enabled ? 'org-studio__stream-row--on' : 'org-studio__stream-row--off',
                    ]"
                  >
                    <span class="org-studio__stream-icon">
                      <v-icon :icon="row.icon" size="18" />
                    </span>
                    <div class="org-studio__stream-copy">
                      <h3 class="org-studio__stream-label">{{ row.label }}</h3>
                      <p v-if="!row.enabled" class="org-studio__stream-hint">Not receiving alerts</p>
                      <p v-else-if="!row.chips?.length" class="org-studio__stream-hint">
                        Enabled · add recipients in edit
                      </p>
                      <div v-if="row.chips?.length" class="org-studio__tags">
                        <span
                          v-for="(chip, idx) in row.chips"
                          :key="`${row.key}-${idx}`"
                          class="org-studio__tag"
                        >
                          {{ chip }}
                        </span>
                      </div>
                    </div>
                    <span
                      class="org-studio__pill"
                      :class="row.enabled ? 'org-studio__pill--on' : 'org-studio__pill--off'"
                    >
                      {{ row.enabled ? 'On' : 'Off' }}
                    </span>
                  </article>
                </div>
              </div>
            </section>
          </div>

          <section class="org-studio__band org-studio__band--defaults">
            <div class="org-studio__rail" aria-hidden="true">
              <span class="org-studio__step">05</span>
            </div>
            <div class="org-studio__panel">
              <header class="org-studio__head org-studio__head--row">
                <div>
                  <span class="org-studio__kicker">Defaults</span>
                  <h2 class="org-studio__title">Lead defaults for new products</h2>
                </div>
                <v-btn
                  variant="flat"
                  color="primary"
                  size="small"
                  prepend-icon="mdi-tune-variant"
                  class="text-none btn-primary-cta"
                  rounded="lg"
                  @click="showDefaultsDrawer = true"
                >
                  Manage defaults
                </v-btn>
              </header>

              <div class="org-studio__stream">
                <article class="org-studio__stream-row org-studio__stream-row--rule org-studio__stream-row--on">
                  <span class="org-studio__stream-icon">
                    <v-icon icon="mdi-form-textbox" size="18" />
                  </span>
                  <div class="org-studio__stream-copy">
                    <h3 class="org-studio__stream-label">Default fields</h3>
                    <p class="org-studio__stream-hint">
                      {{
                        defaultFieldChips.length
                          ? 'Copied into every newly created product'
                          : 'Platform baseline — the 7 standard fields'
                      }}
                    </p>
                    <div v-if="defaultFieldChips.length" class="org-studio__tags">
                      <template v-for="chip in defaultFieldChips" :key="`df-${chip.key}`">
                        <v-tooltip
                          v-if="chip.options.length"
                          location="bottom"
                          max-width="360"
                          content-class="org-defaults__tooltip"
                        >
                          <template #activator="{ props: tipProps }">
                            <span
                              class="org-studio__tag org-defaults__tag org-defaults__tag--dropdown"
                              v-bind="tipProps"
                            >
                              <v-icon :icon="chip.icon" size="12" />
                              {{ chip.label }}
                              <span class="org-defaults__tag-type">{{ chip.typeShort }}</span>
                              <v-icon icon="mdi-chevron-down" size="12" class="org-defaults__tag-caret" />
                            </span>
                          </template>
                          <div class="org-defaults__tooltip-body">
                            <span class="org-defaults__tooltip-title">
                              {{ chip.label }} options
                            </span>
                            <div class="org-defaults__tooltip-options">
                              <span
                                v-for="option in chip.options"
                                :key="`${chip.key}-${option}`"
                                class="org-defaults__tooltip-option"
                              >
                                {{ option }}
                              </span>
                            </div>
                          </div>
                        </v-tooltip>
                        <span v-else class="org-studio__tag org-defaults__tag">
                          <v-icon :icon="chip.icon" size="12" />
                          {{ chip.label }}
                          <span class="org-defaults__tag-type">{{ chip.typeShort }}</span>
                        </span>
                      </template>
                    </div>
                  </div>
                </article>

                <article class="org-studio__stream-row org-studio__stream-row--rule org-studio__stream-row--on">
                  <span class="org-studio__stream-icon">
                    <v-icon icon="mdi-list-status" size="18" />
                  </span>
                  <div class="org-studio__stream-copy">
                    <h3 class="org-studio__stream-label">Default lead statuses</h3>
                    <p class="org-studio__stream-hint">
                      {{
                        defaultStatusChips.length
                          ? 'New products start with this status list'
                          : 'Platform baseline — Pending, Contacted, Converted, Rejected'
                      }}
                    </p>
                    <div v-if="defaultStatusChips.length" class="org-studio__tags">
                      <span
                        v-for="chip in defaultStatusChips"
                        :key="`ds-${chip}`"
                        class="org-studio__tag"
                      >
                        {{ chip }}
                      </span>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </section>
        </div>

        <div v-show="outgoingEnabled && pageTab === 'settings'" class="org-studio__brief">
          <section class="org-studio__band org-studio__band--story">
            <div class="org-studio__rail" aria-hidden="true">
              <span class="org-studio__step">01</span>
              <span class="org-studio__spine" />
            </div>
            <div class="org-studio__panel">
              <header class="org-studio__head org-studio__head--row">
                <div>
                  <span class="org-studio__kicker">Reports</span>
                  <h2 class="org-studio__title">Dashboard Digest</h2>
                </div>
                <v-btn
                  variant="flat"
                  color="primary"
                  size="small"
                  prepend-icon="mdi-content-save-outline"
                  class="text-none btn-primary-cta"
                  rounded="lg"
                  :loading="savingDigest"
                  :disabled="savingDigest"
                  @click="saveDigestSettings"
                >
                  Save
                </v-btn>
              </header>
              <p class="org-page__settings-desc">
                Periodically email/WhatsApp org-wide KPIs and agent activity across every product
                and campaign, instead of relying on someone to come check the dashboard.
              </p>
              <DashboardDigestSettings
                v-model="digestSettings"
                v-model:send-now="digestSendNow"
                scope-label="organization"
                :disabled="savingDigest"
              />
            </div>
          </section>
        </div>
      </div>
    </template>

    <OrgDefaultsDrawer
      v-model="showDefaultsDrawer"
      :organization="displayOrganization"
      @saved="onDefaultsSaved"
      @error="onDefaultsError"
    />
  </div>
</template>

<script>
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
import RequestCreditsDialog from "@/components/Credits/RequestCreditsDialog.vue";
import UpdateOrgDetailsDialog from "@/components/Organizations/UpdateOrgDetailsDialog.vue";
import OrgDefaultsDrawer from "@/components/Organizations/OrgDefaultsDrawer.vue";
import DashboardDigestSettings from "@/components/Shared/DashboardDigestSettings.vue";
import organizationDisplayMixin from "@/mixins/Organization/organizationDisplayMixin";
import updateOrganization, { ORG_DESCRIPTION_MAX } from "@/mixins/Organization/updateOrganization";
import { mapGetters } from "vuex";
import {
  loadOrgLogoPreviewUrl,
  revokeOrgLogoPreviewUrl,
} from "@/utils/orgLogoStorage";
import { resolveOrgLogoS3Key } from "@/utils/orgLogo";
import { triggerDashboardDigestNow } from "@/api/dashboardDigestApi";
import { isApiSuccess } from "@/mixins/Extras/ApiResponse";
import { formatLabel, formatNumber, formatRupee, formatPhoneNumber, formatPhoneNumbers, formatUserContactDisplay } from "@/utils/format";
import "@/styles/organizations/org-details.css";

export default {
  name: "OrgDetailsPage",
  components: {
    SnackbarComp,
    DashboardDigestSettings,
    RequestCreditsDialog,
    UpdateOrgDetailsDialog,
    OrgDefaultsDrawer,
  },
  mixins: [organizationDisplayMixin, updateOrganization],
  data() {
    return {
      SnackbarComponent: {},
      pageTab: "details",
      showEditDialog: false,
      showRequestCreditsDialog: false,
      showDefaultsDrawer: false,
      logoPreviewUrl: null,
      logoPreviewIsBlob: false,
      savingDigest: false,
      digestSendNow: false,
      digestSettingsForm: {
        send_dashboard_digest_by_email: false,
        dashboard_digest_email_addresses: [],
        send_dashboard_digest_by_whatsapp: false,
        dashboard_digest_whatsapp_numbers: [],
        dashboard_digest_cc_agents: false,
        dashboard_digest_times: [],
        dashboard_digest_weekly_enabled: false,
        dashboard_digest_weekly_day: "",
        dashboard_digest_weekly_time: "",
        dashboard_digest_monthly_enabled: false,
        dashboard_digest_monthly_day: null,
        dashboard_digest_monthly_time: "",
      },
    };
  },
  computed: {
    ...mapGetters(["sessionContext"]),
    // Same BMS-controlled gate LandingPage.vue's own nav uses to hide
    // Campaigns/Contexts/BusinessQueries for orgs without outbound calling --
    // Dashboard Digest reports on exactly that data, so it's meaningless for
    // those same orgs.
    outgoingEnabled() {
      return this.displayOrganization?.outgoing_enabled !== false;
    },
    // Cheap fingerprint of just the 6 digest fields on the merged org record.
    // The sync watcher below keys off THIS, not displayOrganization.org_id --
    // org_id is set early by the lightweight session bootstrap
    // (getCurrentUserDetails, see store's SET_SESSION) before the fuller
    // getOrgDetails load ever runs, and org_id itself doesn't change when
    // that fuller load lands moments later with the SAME org_id. Watching
    // org_id alone meant the richer digest data arriving after the initial
    // sync was silently never picked up -- same bug CampaignDetailPage.vue
    // had (there, two separate loaders both wrote campaign.value but only
    // one of them re-synced the digest form off it).
    digestFieldsSignature() {
      const org = this.displayOrganization ?? {};
      return JSON.stringify([
        org.send_dashboard_digest_by_email,
        org.dashboard_digest_email_addresses,
        org.send_dashboard_digest_by_whatsapp,
        org.dashboard_digest_whatsapp_numbers,
        org.dashboard_digest_cc_agents,
        org.dashboard_digest_times,
        org.dashboard_digest_weekly_enabled,
        org.dashboard_digest_weekly_day,
        org.dashboard_digest_weekly_time,
        org.dashboard_digest_monthly_enabled,
        org.dashboard_digest_monthly_day,
        org.dashboard_digest_monthly_time,
      ]);
    },
    // DashboardDigestSettings takes one v-model object for its six fields --
    // same proxy pattern UpdateOrgDetailsDialog.vue used to use before this
    // moved out to its own page-level tab.
    digestSettings: {
      get() {
        return { ...this.digestSettingsForm };
      },
      set(next) {
        Object.assign(this.digestSettingsForm, next);
      },
    },
    statusDisplay() {
      return formatLabel(this.displayOrganization.org_status);
    },
    statusTone() {
      const status = String(
        this.displayOrganization.org_status || "",
      ).toUpperCase();
      if (status === "ACTIVE") return "active";
      if (status === "INACTIVE" || status === "DEACTIVATED") return "inactive";
      return "neutral";
    },
    orgInitials() {
      const name = String(this.displayOrganization.org_name || "O").trim();
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
    descriptionText() {
      const description = this.displayOrganization.org_description?.trim();
      return description ? description.slice(0, ORG_DESCRIPTION_MAX) : "";
    },
    orgOverviewCards() {
      const org = this.displayOrganization ?? {};
      return [
        {
          key: "total_calls",
          label: "Total calls",
          value:
            org.total_number_of_calls != null
              ? formatNumber(org.total_number_of_calls)
              : "—",
          icon: "mdi-phone-in-talk",
        },
        {
          key: "daily_rental",
          label: "Daily rental",
          value: org.daily_rental != null ? formatRupee(org.daily_rental) : "—",
          icon: "mdi-calendar-clock-outline",
        },
        {
          key: "cost_per_minute",
          label: "Per minute",
          value:
            org.voice_agent_cost_per_minute != null
              ? `${formatRupee(org.voice_agent_cost_per_minute)}`
              : "—",
          icon: "mdi-microphone-outline",
        },
        {
          key: "credits_balance",
          label: "Credits",
          value:
            org.credits_balance != null
              ? formatNumber(org.credits_balance)
              : "—",
          icon: "mdi-wallet-outline",
        },
      ];
    },
    phoneDisplay() {
      // org_full_number is written ONCE, at onboarding (onBoardOrganizations,
      // yandle_bms_portal_1) -- editing the org afterward only ever updates
      // org_country_code/org_contact_number, never that field, so it goes
      // stale the moment an admin changes their number. Reading it here made
      // an edit that saved correctly (confirmed live 2026-08-15: DB held the
      // new org_contact_number) LOOK like it silently failed, because this
      // page kept showing the old number from org_full_number regardless.
      // org_country_code/org_contact_number are always current -- derive
      // from those only.
      const code = this.displayOrganization.org_country_code;
      const num = this.displayOrganization.org_contact_number;
      if (code && num) {
        return formatUserContactDisplay(code, num) || formatPhoneNumber(num);
      }
      return num ? formatPhoneNumber(num) : "";
    },
    addressLine() {
      const org = this.displayOrganization;
      const parts = [
        org.org_address,
        org.org_city,
        org.org_state,
        org.org_pincode,
      ]
        .map((part) => String(part || "").trim())
        .filter(Boolean);
      return parts.join(", ");
    },
    contactRows() {
      const rows = [];
      const email = this.displayOrganization.org_contact_email?.trim();
      if (email) {
        rows.push({
          key: "email",
          label: "Email",
          icon: "mdi-email-outline",
          text: email,
          href: `mailto:${email}`,
        });
      }
      if (this.phoneDisplay) {
        rows.push({
          key: "phone",
          label: "Phone",
          icon: "mdi-phone-outline",
          text: this.phoneDisplay,
        });
      }
      const sip =
        this.displayOrganization.sip_number?.trim() ||
        this.displayOrganization.assigned_sip_number?.trim();
      if (sip) {
        rows.push({
          key: "sip",
          label: "SIP",
          icon: "mdi-phone-voip",
          text: formatPhoneNumber(sip),
        });
      }
      const website = this.displayOrganization.org_website_url?.trim();
      if (website) {
        rows.push({
          key: "web",
          label: "Website",
          icon: "mdi-web",
          text: website.replace(/^https?:\/\//i, ""),
          href: website,
        });
      }
      if (this.addressLine) {
        rows.push({
          key: "address",
          label: "Address",
          icon: "mdi-map-marker-outline",
          text: this.addressLine,
          wide: true,
        });
      }
      return rows;
    },
    callPreferenceRows() {
      const org = this.displayOrganization;
      const emailExtras = Array.isArray(org.email_addresses)
        ? org.email_addresses.filter(Boolean)
        : [];
      const whatsappExtras = formatPhoneNumbers(
        Array.isArray(org.whatsapp_numbers)
          ? org.whatsapp_numbers.filter(Boolean)
          : [],
      )

      return [
        {
          key: "call_logs_email",
          label: "Email call logs",
          icon: "mdi-email-sync-outline",
          channel: "email",
          enabled: Boolean(org.send_call_logs_by_email),
          isChannel: true,
          chips: org.send_call_logs_by_email ? emailExtras : [],
          text:
            org.send_call_logs_by_email && !emailExtras.length
              ? "Sent to organisation contact email"
              : "",
        },
        {
          key: "call_logs_whatsapp",
          label: "WhatsApp call logs",
          icon: "mdi-whatsapp",
          channel: "whatsapp",
          enabled: Boolean(org.send_call_logs_by_whatsapp),
          isChannel: true,
          chips: org.send_call_logs_by_whatsapp ? whatsappExtras : [],
        },
        {
          key: "restrict_incoming_calls",
          label: "Restrict incoming calls",
          icon: "mdi-shield-lock-outline",
          channel: "rule",
          text: org.restrict_incoming_calls
            ? "Only callers already in your Callers list"
            : "All callers allowed",
          enabled: Boolean(org.restrict_incoming_calls),
          isStatus: true,
        },
      ];
    },
    defaultFieldChips() {
      let definitions = this.displayOrganization.default_field_definitions;
      if (typeof definitions === "string") {
        try {
          definitions = JSON.parse(definitions);
        } catch {
          definitions = null;
        }
      }
      if (!definitions || typeof definitions !== "object") return [];

      const TYPE_META = {
        TEXT: { icon: "mdi-format-text", short: "Text" },
        NUMBER: { icon: "mdi-numeric", short: "Number" },
        DROPDOWN: { icon: "mdi-form-dropdown", short: "Dropdown" },
        PHONE_NUMBER: { icon: "mdi-phone-outline", short: "Phone" },
        DATE: { icon: "mdi-calendar-outline", short: "Date" },
      };

      return Object.entries(definitions).map(([key, definition]) => {
        const type = String(definition?.type ?? "TEXT").toUpperCase();
        const meta = TYPE_META[type] || TYPE_META.TEXT;
        const options = (Array.isArray(definition?.options)
          ? definition.options
          : []
        ).filter(Boolean);
        return {
          key,
          label: String(definition?.label ?? "").trim() || key,
          typeShort: meta.short,
          icon: meta.icon,
          options: type === "DROPDOWN" ? options : [],
        };
      });
    },
    defaultStatusChips() {
      const statuses = this.displayOrganization.default_lead_statuses;
      return (Array.isArray(statuses) ? statuses : [])
        .map((status) => formatLabel(status))
        .filter(Boolean);
    },
    businessQueryRows() {
      const org = this.displayOrganization;
      const emails = Array.isArray(org.business_query_email_addresses)
        ? org.business_query_email_addresses.filter(Boolean)
        : [];
      const numbers = formatPhoneNumbers(
        Array.isArray(org.business_query_whatsapp_numbers)
          ? org.business_query_whatsapp_numbers.filter(Boolean)
          : [],
      )

      return [
        {
          key: "bq_email",
          label: "Email alerts",
          icon: "mdi-email-outline",
          channel: "email",
          enabled: Boolean(org.send_business_queries_by_email),
          isChannel: true,
          chips: org.send_business_queries_by_email ? emails : [],
        },
        {
          key: "bq_whatsapp",
          label: "WhatsApp alerts",
          icon: "mdi-whatsapp",
          channel: "whatsapp",
          enabled: Boolean(org.send_business_queries_by_whatsapp),
          isChannel: true,
          chips: org.send_business_queries_by_whatsapp ? numbers : [],
        },
      ];
    },
  },
  beforeUnmount() {
    revokeOrgLogoPreviewUrl(this.logoPreviewUrl, this.logoPreviewIsBlob);
  },
  watch: {
    "displayOrganization.org_logo_s3_key": {
      immediate: true,
      handler(key) {
        this.loadLogoPreview(key);
      },
    },
    // Fires once org data actually loads, AND again any time the digest
    // fields specifically change value (see digestFieldsSignature) -- e.g.
    // the fuller getOrgDetails load landing after the lightweight session
    // bootstrap already set org_id. Keying off the signature instead of
    // org_id directly still won't stomp an in-progress edit here from an
    // unrelated background store refresh (org_name, sip_number, etc.
    // changing doesn't touch this fingerprint), matching the original intent.
    digestFieldsSignature: {
      immediate: true,
      handler() {
        if (this.displayOrganization?.org_id) this.syncDigestSettingsFromOrg();
      },
    },
  },
  methods: {
    syncDigestSettingsFromOrg() {
      const org = this.displayOrganization;
      this.digestSettingsForm = {
        send_dashboard_digest_by_email: org.send_dashboard_digest_by_email === true,
        dashboard_digest_email_addresses: Array.isArray(org.dashboard_digest_email_addresses)
          ? [...org.dashboard_digest_email_addresses]
          : [],
        send_dashboard_digest_by_whatsapp: org.send_dashboard_digest_by_whatsapp === true,
        dashboard_digest_whatsapp_numbers: Array.isArray(org.dashboard_digest_whatsapp_numbers)
          ? [...org.dashboard_digest_whatsapp_numbers]
          : [],
        dashboard_digest_cc_agents: org.dashboard_digest_cc_agents === true,
        dashboard_digest_times: Array.isArray(org.dashboard_digest_times)
          ? [...org.dashboard_digest_times]
          : [],
        dashboard_digest_weekly_enabled: org.dashboard_digest_weekly_enabled === true,
        dashboard_digest_weekly_day: org.dashboard_digest_weekly_day || "",
        dashboard_digest_weekly_time: org.dashboard_digest_weekly_time || "",
        dashboard_digest_monthly_enabled: org.dashboard_digest_monthly_enabled === true,
        dashboard_digest_monthly_day: org.dashboard_digest_monthly_day ?? null,
        dashboard_digest_monthly_time: org.dashboard_digest_monthly_time || "",
      };
    },
    async saveDigestSettings() {
      this.savingDigest = true;
      try {
        // Digest-only input -- no org_id/org_name needed (UpdateOrgProfileInput
        // has no org_id field at all; the backend resolves the caller's own
        // org from current_user_id), and every other org field is simply
        // omitted rather than sent as a stale/default value.
        const response = await this.updateOrganization({ ...this.digestSettingsForm });
        if (!isApiSuccess(response)) {
          this.onError(response, "Could not save Dashboard Digest settings.");
          return;
        }
        await this.refreshOrganizationFromApi();

        if (this.digestSendNow) {
          const sendResponse = await triggerDashboardDigestNow({
            scope_type: "ORGANIZATION",
            scope_id: this.displayOrganization.org_id,
          });
          this.digestSendNow = false;
          if (!isApiSuccess(sendResponse)) {
            this.onError(sendResponse, "Settings saved, but the immediate send failed.");
            return;
          }
          this.showSnack("success", "Dashboard Digest settings saved and sent.");
          return;
        }

        this.showSnack("success", "Dashboard Digest settings saved.");
      } catch (err) {
        this.onError(err, "Could not save Dashboard Digest settings.");
      } finally {
        this.savingDigest = false;
      }
    },
    async loadLogoPreview(key) {
      revokeOrgLogoPreviewUrl(this.logoPreviewUrl, this.logoPreviewIsBlob);
      this.logoPreviewUrl = null;
      this.logoPreviewIsBlob = false;

      const path = String(key || resolveOrgLogoS3Key(this.displayOrganization)).trim();
      if (!path) return;

      try {
        const { url, isBlob } = await loadOrgLogoPreviewUrl(path);
        if (url && path === resolveOrgLogoS3Key(this.displayOrganization)) {
          this.logoPreviewUrl = url;
          this.logoPreviewIsBlob = isBlob;
        }
      } catch (err) {
        console.log("Org details page logo load failed", err);
      }
    },
    onOrgDetailsError(payload) {
      this.onError(payload, "Failed to save org details.");
    },
    onDefaultsError(message) {
      this.showSnack("error", message || "Failed to save lead defaults.");
    },
    onDefaultsSaved({ message, definitions, statuses }) {
      // The drawer already holds the updated state — patch the store so the
      // band summary refreshes without a full org-profile round-trip.
      const update = {};
      if (definitions && typeof definitions === "object") {
        update.default_field_definitions = definitions;
      }
      if (Array.isArray(statuses)) {
        update.default_lead_statuses = statuses;
      }
      if (Object.keys(update).length) {
        this.$store.commit("UPDATE_ORGANIZATION", update);
      }
      this.showSnack("success", message || "Lead defaults saved.");
    },
    async onOrgDetailsSaved(response) {
      await this.onSaved(response, "Org details saved successfully.");
    },
    onCreditRequestError(payload) {
      this.onError(payload, "Failed to submit credit request");
    },
    async onCreditRequestSaved() {
      this.showSnack(
        "success",
        "Payment successful. Credits will be added after verification."
      );
      try {
        await this.refreshOrganizationFromApi();
      } catch (err) {
        this.onError(err, "Failed to refresh organisation details");
      }
    },
  },
};
</script>

<style scoped>
.org-page__loading {
  flex: 1;
  min-height: 120px;
}

.org-defaults__tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: default;
}

.org-defaults__tag .v-icon {
  opacity: 0.65;
}

.org-defaults__tag-type {
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  opacity: 0.55;
}

.org-defaults__tag--dropdown {
  cursor: help;
}

.org-defaults__tag-caret {
  opacity: 0.5;
}

.org-defaults__tooltip-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 0;
}

.org-defaults__tooltip-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.8;
}

.org-defaults__tooltip-options {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.org-defaults__tooltip-option {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-size: 0.72rem;
  font-weight: 500;
  white-space: nowrap;
}

.org-masthead {
  flex: 0 0 auto;
}

.org-page__tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  margin: 0 0 1.25rem;
  background: var(--y-surface-hover);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
  width: fit-content;
}
.org-page__tab {
  appearance: none;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 650;
  color: var(--y-text-muted);
  cursor: pointer;
  padding: 7px 16px;
  border-radius: 7px;
  transition: background 0.12s ease, color 0.12s ease;
}
.org-page__tab:hover {
  color: var(--y-text);
}
.org-page__tab.is-active {
  background: var(--y-surface);
  color: var(--y-text);
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.08);
}
.org-page__settings-desc {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--y-text-muted);
  max-width: 42rem;
}
</style>
